<template>
    <div class="compass-performance-chart">
        <!-- Metric Tabs -->
        <div class="analytics-tabs mb-3" style="font-size: 0.85rem;">
            <button
                v-for="tab in tabs"
                :key="tab.key"
                class="tab-btn"
                :class="{ active: activeTab === tab.key }"
                @click="activeTab = tab.key"
            >
                {{ tab.label }}
            </button>
        </div>

        <div v-if="loading" class="text-center py-4">
            <div class="spinner-border spinner-border-sm text-primary"></div>
            <span class="small text-muted ms-2">Loading performance data...</span>
        </div>

        <div v-else-if="!hasData" class="text-muted small text-center py-3">
            Historical data not yet available.
        </div>

        <canvas v-show="hasData && !loading" ref="chartCanvas" height="220"></canvas>

        <div v-if="hasData && !loading" class="d-flex justify-content-between small text-muted mt-2 px-1">
            <span v-if="currentSummary.min !== null">
                Low: <span class="font-mono-num">{{ currentSummary.min }}</span>{{ currentSummary.unit }}
            </span>
            <span v-if="currentSummary.avg !== null">
                Avg: <span class="font-mono-num fw-bold">{{ currentSummary.avg }}</span>{{ currentSummary.unit }}
            </span>
            <span v-if="currentSummary.max !== null">
                High: <span class="font-mono-num">{{ currentSummary.max }}</span>{{ currentSummary.unit }}
            </span>
        </div>
    </div>
</template>

<script>
import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);

export default {
    name: 'CompassPerformanceChart',
    data() {
        return {
            loading: true,
            chart: null,
            activeTab: 'apy',
            apyHistory: [],
            tokenValueHistory: [],
            tabs: [
                { key: 'apy', label: 'APY History' },
                { key: 'tokenValue', label: 'compassSOL Value' },
                { key: 'tvl', label: 'Total Stake' },
            ],
        };
    },
    computed: {
        hasData() {
            if (this.activeTab === 'apy') return this.apyHistory.length > 2;
            return this.tokenValueHistory.length > 2;
        },
        currentSummary() {
            let data, unit;
            if (this.activeTab === 'apy') {
                data = this.apyHistory.map(d => d.apy);
                unit = '%';
            } else if (this.activeTab === 'tokenValue') {
                data = this.tokenValueHistory.map(d => d.tokenValue);
                unit = ' SOL';
            } else {
                data = this.tokenValueHistory.map(d => d.totalSol);
                unit = ' SOL';
            }
            if (!data.length) return { min: null, avg: null, max: null, unit: '' };
            const min = Math.min(...data);
            const max = Math.max(...data);
            const avg = data.reduce((s, v) => s + v, 0) / data.length;
            const decimals = this.activeTab === 'tokenValue' ? 4 : this.activeTab === 'tvl' ? 0 : 2;
            return {
                min: min.toFixed(decimals),
                avg: avg.toFixed(decimals),
                max: max.toFixed(decimals),
                unit,
            };
        },
    },
    watch: {
        activeTab() {
            this.renderChart();
        },
    },
    async mounted() {
        await this.fetchData();
        this.renderChart();
    },
    beforeUnmount() {
        if (this.chart) this.chart.destroy();
    },
    methods: {
        async fetchData() {
            this.loading = true;
            try {
                const resp = await fetch('/app/stake/history');
                if (!resp.ok) throw new Error('Failed to load');
                const data = await resp.json();
                this.apyHistory = data.apyHistory || [];
                this.tokenValueHistory = data.tokenValueHistory || [];
            } catch (e) {
                console.warn('[CompassPerformanceChart] Failed to load history:', e);
            } finally {
                this.loading = false;
            }
        },

        renderChart() {
            if (!this.$refs.chartCanvas) return;
            if (this.chart) this.chart.destroy();

            const ctx = this.$refs.chartCanvas.getContext('2d');
            let labels, values, label, borderColor, bgColor, yUnit;

            if (this.activeTab === 'apy') {
                const data = this.apyHistory.slice(-80); // last 80 epochs
                labels = data.map(d => `E${d.epoch}`);
                values = data.map(d => d.apy);
                label = 'Staking APY (%)';
                borderColor = 'rgb(16, 185, 129)';
                bgColor = 'rgba(16, 185, 129, 0.1)';
                yUnit = '%';
            } else if (this.activeTab === 'tokenValue') {
                const data = this.tokenValueHistory.slice(-80);
                labels = data.map(d => `E${d.epoch}`);
                values = data.map(d => d.tokenValue);
                label = 'compassSOL Value (SOL)';
                borderColor = 'rgb(99, 102, 241)';
                bgColor = 'rgba(99, 102, 241, 0.1)';
                yUnit = ' SOL';
            } else {
                const data = this.tokenValueHistory.slice(-80);
                labels = data.map(d => `E${d.epoch}`);
                values = data.map(d => d.totalSol);
                label = 'Total Staked SOL';
                borderColor = 'rgb(245, 158, 11)';
                bgColor = 'rgba(245, 158, 11, 0.1)';
                yUnit = ' SOL';
            }

            if (!values.length) return;

            this.chart = new Chart(ctx, {
                type: 'line',
                data: {
                    labels,
                    datasets: [{
                        label,
                        data: values,
                        borderColor,
                        backgroundColor: bgColor,
                        fill: true,
                        tension: 0.3,
                        borderWidth: 2,
                        pointRadius: 0,
                        pointHoverRadius: 4,
                    }],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: { mode: 'index', intersect: false },
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            callbacks: {
                                label: (ctx) => {
                                    const val = ctx.parsed.y;
                                    if (this.activeTab === 'tvl') return `${label}: ${val.toLocaleString()}${yUnit}`;
                                    return `${label}: ${val.toFixed(this.activeTab === 'tokenValue' ? 4 : 2)}${yUnit}`;
                                },
                            },
                        },
                    },
                    scales: {
                        x: {
                            grid: { display: false },
                            ticks: {
                                maxTicksLimit: 10,
                                font: { size: 11 },
                                color: '#888',
                            },
                        },
                        y: {
                            grid: { color: 'rgba(255,255,255,0.05)' },
                            ticks: {
                                font: { size: 11 },
                                color: '#888',
                                callback: (val) => {
                                    if (this.activeTab === 'tvl') return val.toLocaleString();
                                    return val.toFixed(this.activeTab === 'tokenValue' ? 4 : 2) + yUnit;
                                },
                            },
                        },
                    },
                },
            });
        },
    },
};
</script>
